import { z } from "zod";
import { SeveritySchema } from "./rule.js";
/**
 * What the agent writes to findings.json. snake_case because it is produced by
 * a language model, and the field names appear verbatim in the prompt contract.
 */
export const FindingSchema = z.object({
    rule_id: z.string().min(1),
    file: z.string().min(1),
    line: z.number().int().positive(),
    severity: SeveritySchema,
    message: z.string().min(1),
});
/** A problem no rule covers yet. Never becomes an inline comment. */
export const ObservationSchema = z.object({
    file: z.string().min(1),
    line: z.number().int().positive(),
    message: z.string().min(1),
    suggested_rule: z.string().optional(),
});
/**
 * A remark about the MR as a whole: is the chosen approach appropriate, does it
 * fit the existing architecture, would it be substantially simpler another way.
 * Deliberately without file/line — these never become inline discussions, they
 * go into their own section of the summary comment.
 */
export const ArchitectureTopicSchema = z.enum(["approach", "architecture", "simplification"]);
export const ArchitectureNoteSchema = z.object({
    topic: ArchitectureTopicSchema,
    /** One line, the remark itself. */
    title: z.string().min(1),
    /** The reasoning, including the existing pattern or the simpler alternative. */
    message: z.string().min(1),
    /** Repo-relative paths the remark is about; may be empty. Never a line number. */
    files: z.array(z.string()).default([]),
});
/** A gitleaks hit outside the diff — never posted to the MR, see PRODUCT.md on noise. */
export const SecretOutsideDiffSchema = z.object({
    fingerprint: z.string().min(1),
    file: z.string().min(1),
    line: z.number().int().positive(),
    description: z.string().min(1),
});
/**
 * How much of a review's cost was actually measured.
 *
 * "measured" — every agent step reported its own price.
 * "partial"  — at least one step did not, so costUsd is a lower bound. This is
 *              the normal case for a failed run: the step that died reported
 *              nothing.
 * "unknown"  — nothing was measured; costUsd says nothing at all.
 *
 * Kept separate from `costUsd == null` because a sum of measured steps and a sum
 * with a hole in it are both numbers, and only this says which one you are
 * looking at.
 */
export const CostSourceSchema = z.enum(["measured", "partial", "unknown"]);
/** What a run spent, reported by a job that did not finish. */
export const FailedRunMetricsSchema = z.object({
    costUsd: z.number().nonnegative().nullable().default(null),
    costSource: CostSourceSchema.default("unknown"),
    durationS: z.number().int().nonnegative().nullable().default(null),
    tokensIn: z.number().int().nonnegative().nullable().default(null),
    tokensOut: z.number().int().nonnegative().nullable().default(null),
});
export const RunMetricsSchema = z.object({
    durationS: z.number().int().nonnegative(),
    costUsd: z.number().nonnegative().nullable(),
    costSource: CostSourceSchema.default("unknown"),
    /** Summed over every opencode run of this review; null on runs before tracking existed. */
    tokensIn: z.number().int().nonnegative().nullable().default(null),
    tokensOut: z.number().int().nonnegative().nullable().default(null),
    promptBytes: z.number().int().nonnegative(),
    diffBytes: z.number().int().nonnegative(),
    changedFiles: z.number().int().nonnegative(),
    commentableLines: z.number().int().nonnegative(),
    /** How many subagents the lead actually spawned. Null on a single-agent run. */
    subagentCount: z.number().int().nonnegative().nullable().default(null),
    /** Whether a coverage gap forced a second pass over the missed files. */
    followupRan: z.boolean().default(false),
    /** Whether the architecture step ran at all; null on runs before it existed. */
    architectureRan: z.boolean().default(false),
});
export const DeliverySchema = z.enum(["inline", "summary"]);
export const DeliveredFindingSchema = FindingSchema.extend({
    discussionId: z.string().nullable(),
    delivered: DeliverySchema,
});
/** The agent's report back to the backend. */
export const ReviewResultSchema = z.object({
    reviewedSha: z.string().min(7),
    findings: z.array(DeliveredFindingSchema),
    observations: z.array(ObservationSchema),
    architectureNotes: z.array(ArchitectureNoteSchema).default([]),
    metrics: RunMetricsSchema,
    resolvedDiscussionIds: z.array(z.string()).default([]),
    /** Whether gitleaks actually ran a full-repo scan this review; distinguishes
     * "scanned, found nothing" from "disabled/not installed" so the backend never
     * resolves known secrets just because a run didn't scan for them. */
    secretsOutsideDiffScanned: z.boolean().default(false),
    secretsOutsideDiff: z.array(SecretOutsideDiffSchema).default([]),
});
